import { normalizeResolvedPath } from '../../../paths/normalize-resolved-path.js';
import { yieldToEventLoop } from '../../../runtime/yield-to-event-loop.js';
import { withCodeNavCacheSqlite } from '../../../sqlite/code-nav-cache/database.js';
import { createSnapshotNavigationLookups, type NavigationLookups } from '../../resolution/navigation/navigation-lookups.js';
import { buildNavigationContext, resolveNavigation } from '../../resolution/resolve-definitions.js';
import type { DbTagRow } from '../../resolution/types.js';
import type { CodeNavDefinitionsResult, CodeNavLocation } from '../../types.js';
import { listWorkspaceFiles } from '../build/workspace-files.js';
import { getSymbolIndexParentContext } from '../sqlite/symbol-index-sqlite.js';
import { loadFileTags } from '../storage/symbol-parent-file-map.js';
import { findTagsAtPosition, pickPrimaryTagAtPosition } from './tag-position.js';
import { modulePathLocation, rowToLocation } from './tag-query-shared.js';

export async function queryDefinitionsNavigation(
  parentPath: string,
  filePath: string,
  line: number,
  column: number,
): Promise<CodeNavDefinitionsResult> {
  await yieldToEventLoop();
  const resolved = normalizeResolvedPath(parentPath);

  const snapshot = await withCodeNavCacheSqlite((db) => {
    const ctx = getSymbolIndexParentContext(db, resolved);
    if (ctx == null) return null;

    const fileRows = loadFileTags(db, ctx, filePath);
    const primary = pickPrimaryTagAtPosition(fileRows, filePath, line, column);
    if (!primary) return null;

    const atPos = findTagsAtPosition(fileRows, filePath, line, column);
    const lookups = createSnapshotNavigationLookups(db, ctx.parentId);
    const workspaceFiles = listWorkspaceFiles(db, resolved, filePath);
    return { primary, atPos, lookups, workspaceFiles };
  });

  if (snapshot == null) {
    await yieldToEventLoop();
    return { definitions: [], confidentTarget: false };
  }

  const result = resolveDefinitionsFromSnapshot(filePath, snapshot);
  await yieldToEventLoop();
  return result;
}

function resolveDefinitionsFromSnapshot(
  filePath: string,
  snapshot: {
    primary: DbTagRow;
    atPos: DbTagRow[];
    lookups: NavigationLookups;
    workspaceFiles: ReadonlySet<string>;
  },
): CodeNavDefinitionsResult {
  const { primary, atPos, lookups, workspaceFiles } = snapshot;

  if (primary.kind === 'module' && primary.nav_target_path) {
    return { definitions: [modulePathLocation(primary)], confidentTarget: true };
  }

  const onDefinition = atPos.some((row) => row.role === 'def');
  if (onDefinition) {
    const defs = atPos.filter((row) => row.role === 'def').map(rowToLocation);
    return { definitions: defs, confidentTarget: defs.length === 1 };
  }

  const navCtx = buildNavigationContext(filePath, primary, lookups, workspaceFiles);
  const navigation = resolveNavigation(navCtx);
  return {
    definitions: navigation.definitions.map(rowToLocation),
    confidentTarget: navigation.confidentTarget,
  };
}

export async function queryDefinitionsAt(
  parentPath: string,
  filePath: string,
  line: number,
  column: number,
): Promise<CodeNavLocation[]> {
  return (await queryDefinitionsNavigation(parentPath, filePath, line, column)).definitions;
}

export async function queryDefinitionAt(
  parentPath: string,
  filePath: string,
  line: number,
  column: number,
): Promise<CodeNavLocation | null> {
  return (await queryDefinitionsAt(parentPath, filePath, line, column))[0] ?? null;
}
